import React, { useState } from 'react';
import { styled } from '@mui/material';
import {
    RFieldGroup,
    RTextInput,
    REmailInput,
    RPhoneInput,
    RRow,
    RPillButton,
    RErrorText,
    RTextButton,
    RText,
} from 'recruit/components';
import { recruitSignUp } from 'recruit/api/candidate_auth';
import { useRecruitCandidateUser } from 'recruit/context/RecruitCandidateUserContext';

interface CandidateCreateAccountFormProps {
    onSignupInitiated: () => void;
    onSwitchToLogin: () => void;
}

export function CandidateCreateAccountForm({ onSignupInitiated, onSwitchToLogin }: CandidateCreateAccountFormProps) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { setPendingSignup } = useRecruitCandidateUser();

    async function handleSubmit() {
        setError('');

        if (!firstName || !lastName || !email || !phone) {
            setError('All fields are required.');
            return;
        }

        setLoading(true);
        try {
            await recruitSignUp({
                first_name: firstName,
                last_name: lastName,
                email,
                phone_number: phone,
            });
            setPendingSignup({
                first_name: firstName,
                last_name: lastName,
                email,
                phone_number: phone,
            });
            onSignupInitiated();
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit}>
                <FieldStack>
                    <RRow gap="lg" align="stretch">
                        <RFieldGroup label="First Name" style={{ flex: 1 }}>
                            <RTextInput value={firstName} onChange={setFirstName} placeholder="First name" fullWidth />
                        </RFieldGroup>
                        <RFieldGroup label="Last Name" style={{ flex: 1 }}>
                            <RTextInput value={lastName} onChange={setLastName} placeholder="Last name" fullWidth />
                        </RFieldGroup>
                    </RRow>
                    <RFieldGroup label="Email">
                        <REmailInput value={email} onChange={(val) => setEmail(val)} validateOnBlur fullWidth />
                    </RFieldGroup>
                    <RFieldGroup label="Phone Number">
                        <RPhoneInput value={phone} onChange={(e164) => setPhone(e164)} fullWidth />
                    </RFieldGroup>
                    {error && <RErrorText>{error}</RErrorText>}
                    <RPillButton onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Sending verification...' : 'Continue'}
                    </RPillButton>
                </FieldStack>
            </form>
            <Row>
                <RText>Already have an account? </RText>
                <RTextButton onClick={onSwitchToLogin}>Log in</RTextButton>
            </Row>
        </>
    );
}

const FieldStack = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const Row = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
});
